import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminJson, getAdminSession } from "../utils/adminSession";
import AdminConfirmDialog from "../components/AdminConfirmDialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_LEN = 8;
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWER = "abcdefghijklmnopqrstuvwxyz";
const NUMBERS = "0123456789";
const SYMBOLS = "!@#$%^&*?";
const ALL = `${UPPER}${LOWER}${NUMBERS}${SYMBOLS}`;
const inputClass =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark outline-none focus:border-at-gold";

function randomChar(set) {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return set[bytes[0] % set.length];
}

function shuffle(chars) {
  const list = [...chars];
  for (let i = list.length - 1; i > 0; i -= 1) {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const j = bytes[0] % (i + 1);
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list.join("");
}

function generatePassword() {
  const required = [randomChar(UPPER), randomChar(LOWER), randomChar(NUMBERS), randomChar(SYMBOLS)];
  while (required.length < PASSWORD_LEN) required.push(randomChar(ALL));
  return shuffle(required);
}

function passwordChecks(value) {
  const password = String(value || "");
  return {
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
    length: password.length >= PASSWORD_LEN,
  };
}

function isValidPassword(value) {
  return String(value || "").length >= PASSWORD_LEN;
}

function initials(item) {
  const fromName = String(item?.name || "").trim();
  if (fromName) {
    const parts = fromName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    return fromName.slice(0, 2).toUpperCase();
  }
  const local = String(item?.email || "").split("@")[0] || "A";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function PasswordField({ id, label, value, onChange, onGenerate, show, onToggleShow }) {
  const checks = passwordChecks(value);
  const score = [checks.upper, checks.lower, checks.number, checks.symbol, checks.length].filter(Boolean).length;
  const strength =
    score <= 2 ? "Weak" : score === 5 ? "Strong" : "Fair";
  const barClass = score <= 2 ? "bg-red-400" : score === 5 ? "bg-emerald-500" : "bg-amber-400";

  async function copyPassword() {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // ignore clipboard failures
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-at-dark" htmlFor={id}>
        {label}
      </label>
      <div className="mt-1.5 flex gap-2">
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            type={show ? "text" : "password"}
            value={value}
            autoComplete="new-password"
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClass} pr-16`}
          />
          <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
            <button type="button" onClick={onToggleShow} className="rounded p-1 text-neutral-400 hover:text-at-dark" aria-label={show ? "Hide password" : "Show password"}>
              <EyeIcon />
            </button>
            <button type="button" onClick={copyPassword} className="rounded p-1 text-neutral-400 hover:text-at-dark" aria-label="Copy password">
              <CopyIcon />
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          className="shrink-0 rounded-md border border-at-gold bg-white px-3 py-2 text-xs font-semibold tracking-wide text-at-dark hover:bg-amber-50"
        >
          GENERATE
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        <CheckLabel ok={checks.length} label={`At least ${PASSWORD_LEN} characters`} />
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100">
        <div className={`h-full ${barClass}`} style={{ width: `${(score / 5) * 100}%` }} />
      </div>
      <p className="mt-1 text-right text-xs text-neutral-500">{strength}</p>
    </div>
  );
}

function CheckLabel({ ok, label }) {
  return (
    <span className={ok ? "text-emerald-700" : "text-neutral-400"}>
      {ok ? "✓" : "○"} {label}
    </span>
  );
}

function AdminAdminsPage() {
  const navigate = useNavigate();
  const session = getAdminSession();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formConfirm, setFormConfirm] = useState("");
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [nameTarget, setNameTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((item) => {
      if (!q) return true;
      return [item.name, item.email].some((value) => String(value || "").toLowerCase().includes(q));
    });
  }, [admins, search]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await adminJson("/admins");
        if (!cancelled) setAdmins(Array.isArray(list) ? list : []);
      } catch (err) {
        if (err?.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        if (!cancelled) setError(err?.message || "Unable to load admins");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  function onUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/login", { replace: true });
      return true;
    }
    return false;
  }

  function openCreate() {
    setFormName("");
    setFormEmail("");
    setFormPassword("");
    setFormConfirm("");
    setShowCreatePassword(false);
    setShowConfirm(false);
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setFormName("");
    setFormEmail("");
    setFormPassword("");
    setFormConfirm("");
  }

  function generateCreatePassword() {
    const next = generatePassword();
    setFormPassword(next);
    setFormConfirm(next);
    setShowCreatePassword(true);
  }

  function generateResetPassword() {
    const next = generatePassword();
    setResetPassword(next);
    setShowResetPassword(true);
  }

  async function onCreate() {
    const email = formEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setError("Enter a valid login email");
      return;
    }
    if (!isValidPassword(formPassword)) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (formPassword !== formConfirm) {
      setError("Passwords do not match");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const created = await adminJson("/admins", {
        method: "POST",
        body: { name: formName.trim(), email, password: formPassword },
      });
      setAdmins((prev) => [...prev, created]);
      setNotice("Admin created. Copy the password now if you have not already.");
      closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to create admin");
    } finally {
      setSaving(false);
    }
  }

  function openReset(item) {
    setResetTarget(item);
    setResetPassword("");
    setShowResetPassword(false);
    setError("");
    setNotice("");
  }

  function closeReset() {
    setResetTarget(null);
    setResetPassword("");
  }

  function openEditName(item) {
    setNameTarget(item);
    setEditName(String(item.name || ""));
    setError("");
    setNotice("");
  }

  function closeEditName() {
    setNameTarget(null);
    setEditName("");
  }

  async function onSaveName() {
    if (!nameTarget) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const updated = await adminJson(`/admins/${nameTarget._id}`, {
        method: "PUT",
        body: { name: editName.trim() },
      });
      setAdmins((prev) => prev.map((row) => (row._id === updated._id ? { ...row, ...updated } : row)));
      setNotice(`Name updated for ${updated.email}.`);
      closeEditName();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to update name");
    } finally {
      setSaving(false);
    }
  }

  async function onReset() {
    if (!resetTarget) return;
    if (!isValidPassword(resetPassword)) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await adminJson(`/admins/${resetTarget._id}`, { method: "PUT", body: { password: resetPassword } });
      setNotice(`Password reset for ${resetTarget.email}.`);
      closeReset();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to reset password");
    } finally {
      setSaving(false);
    }
  }

  function onDelete(item) {
    if (item.email === session?.email) {
      setError("You cannot delete your own account");
      return;
    }
    setError("");
    setDeleteTarget(item);
  }

  async function confirmDelete() {
    const item = deleteTarget;
    if (!item) return;
    setError("");
    setNotice("");
    setSaving(true);
    try {
      await adminJson(`/admins/${item._id}`, { method: "DELETE" });
      setAdmins((prev) => prev.filter((row) => row._id !== item._id));
      setNotice("Admin deleted.");
      setDeleteTarget(null);
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to delete admin");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="-mt-2">
      <p className="text-xs text-neutral-400">Dashboard / Administrators</p>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-at-dark">Administrators</h1>
          <p className="mt-1 text-sm text-neutral-500">Manage access to the AT Industrial Supply admin portal.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
        >
          + Create Admin
        </button>
      </div>

      <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Passwords are securely hashed and can never be viewed. If access is lost, reset the password.
      </div>

      {error && !panelOpen && !resetTarget && !nameTarget && !deleteTarget && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-4 text-sm text-emerald-700">{notice}</p>}

      <div className="mt-6 rounded-xl border border-neutral-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-neutral-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold text-at-dark">Admin Accounts</h2>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or login email..."
            className={`${inputClass} max-w-xs`}
          />
        </div>

        {loading ? (
          <p className="px-4 py-10 text-sm text-neutral-500">Loading admins…</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-10 text-sm text-neutral-500">No admin accounts found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-neutral-100 text-xs font-medium uppercase tracking-wide text-neutral-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Login Email</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const isYou = item.email === session?.email;
                  return (
                    <tr key={item._id} className="border-b border-neutral-100 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-at-dark">
                            {initials(item)}
                          </span>
                          <span className="font-medium text-at-dark">{item.name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-at-dark">{item.email}</span>
                          {isYou ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-at-dark">You</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openEditName(item)}
                            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-3 py-1.5 text-sm text-at-dark hover:border-at-gold"
                          >
                            Edit Name
                          </button>
                          <button
                            type="button"
                            onClick={() => openReset(item)}
                            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-3 py-1.5 text-sm text-at-dark hover:border-at-gold"
                          >
                            Reset Password
                          </button>
                          <button
                            type="button"
                            disabled={isYou}
                            onClick={() => onDelete(item)}
                            className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-4 py-3 text-xs text-neutral-400">
          {admins.length} {admins.length === 1 ? "admin account" : "admin accounts"}
        </p>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
          <button type="button" aria-label="Close panel" className="h-full flex-1" onClick={closePanel} />
          <aside className="flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-neutral-100 px-6 py-5">
              <div>
                <h2 className="text-xl font-semibold text-at-dark">Create New Admin</h2>
                <p className="mt-1 text-sm text-neutral-500">Give a user access to the admin portal.</p>
              </div>
              <button type="button" onClick={closePanel} className="text-2xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close">
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
              <label className="block text-sm font-medium text-at-dark" htmlFor="adminName">
                Name
              </label>
              <input
                id="adminName"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                maxLength={80}
                placeholder="Shown in activity logs"
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1 text-xs text-neutral-400">Used in logs only. Not used to sign in.</p>

              <label className="mt-5 block text-sm font-medium text-at-dark" htmlFor="loginEmail">
                Login Email *
              </label>
              <input
                id="loginEmail"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="name@company.com"
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1 text-xs text-neutral-400">This email will be used to sign in.</p>

              <div className="mt-5">
                <PasswordField
                  id="password"
                  label="Password *"
                  value={formPassword}
                  onChange={setFormPassword}
                  onGenerate={generateCreatePassword}
                  show={showCreatePassword}
                  onToggleShow={() => setShowCreatePassword((prev) => !prev)}
                />
              </div>

              <label className="mt-5 block text-sm font-medium text-at-dark" htmlFor="confirmPassword">
                Confirm Password *
              </label>
              <div className="relative mt-1.5">
                <input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={formConfirm}
                  autoComplete="new-password"
                  spellCheck={false}
                  onChange={(e) => setFormConfirm(e.target.value)}
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((prev) => !prev)}
                  className="absolute inset-y-0 right-1 rounded p-1 text-neutral-400 hover:text-at-dark"
                  aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                >
                  <EyeIcon />
                </button>
              </div>

              <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Copy the generated password before creating the account. It will not be shown again.
              </p>
            </div>
            <div className="flex gap-2 border-t border-neutral-100 px-6 py-4">
              <button type="button" onClick={closePanel} className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={onCreate}
                className="flex-1 rounded-md bg-at-gold px-3 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60"
              >
                {saving ? "Saving…" : "Create Admin"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {nameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-at-dark">Edit Name</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  Display name for {nameTarget.email}. Used in logs only.
                </p>
              </div>
              <button type="button" onClick={closeEditName} className="text-2xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close">
                ×
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="editAdminName">
              Name
            </label>
            <input
              id="editAdminName"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={80}
              placeholder="Shown in activity logs"
              className={`${inputClass} mt-1.5`}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={closeEditName} className="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-at-dark">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={onSaveName}
                className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save Name"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-at-dark">Reset Password?</h2>
                <p className="mt-1 text-sm text-neutral-500">Reset password for {resetTarget.email}.</p>
              </div>
              <button type="button" onClick={closeReset} className="text-2xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close">
                ×
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            <div className="mt-4">
              <PasswordField
                id="resetPassword"
                label="New Password *"
                value={resetPassword}
                onChange={setResetPassword}
                onGenerate={generateResetPassword}
                show={showResetPassword}
                onToggleShow={() => setShowResetPassword((prev) => !prev)}
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={closeReset} className="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-at-dark">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={onReset}
                className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60"
              >
                {saving ? "Saving…" : "Reset Password"}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget ? (
        <AdminConfirmDialog
          title="Delete admin?"
          message={`Delete “${deleteTarget.name || deleteTarget.email}”? You can restore this account from Recent Activities for 30 days.`}
          busy={saving}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
      <circle cx="12" cy="12" r="2.2" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="8" y="8" width="10" height="12" rx="1.5" />
      <path d="M6 16H5a1.5 1.5 0 01-1.5-1.5v-11A1.5 1.5 0 015 2h9A1.5 1.5 0 0115.5 3.5V5" />
    </svg>
  );
}

export default AdminAdminsPage;
